import i18next from "i18next";
import { useCallback, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { RoleEnum } from "src/api";
import { useAuth } from "src/hooks/useAuth";

export function PeriodOpportunityTable({ rows }) {
  const [showAll, setShowAll] = useState(false);
  const { userRole } = useAuth();
  const allRows = rows.slice();
  const displayRows = showAll ? allRows : allRows.slice(0, 5);
  const hasMore = allRows.length > 5;
  const showProvvigioniDealer = [RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER, RoleEnum.SALES_DEALER, RoleEnum.SALES].includes(userRole);
  const isSegnalatore = userRole === RoleEnum.SEGNALATORE;

  const cellStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  const labelProvvigioniSegnalatoreByRole = useMemo(() => {
    switch (userRole) {
      case RoleEnum.MASTER:
      case RoleEnum.BACKOFFICE:
      case RoleEnum.SALES:
      case RoleEnum.SALES_DEALER:
        return i18next.t('provvigioni_segnalatore');
      case RoleEnum.SEGNALATORE:
        return i18next.t('commissions')
      default:
        return i18next.t('provvigioni_segnalatore_euro');
    }
  }, [userRole])

  const labelProvvigioniDealerByRole = useMemo(() => {
    switch (userRole) {
      case RoleEnum.MASTER:
      case RoleEnum.BACKOFFICE:
      case RoleEnum.AREA_MANAGER:
        return i18next.t('provvigioni_dealer_euro');
      case RoleEnum.SALES:
      case RoleEnum.SALES_DEALER:
        return i18next.t('provvigioni_dealer_euro')
      default:
        return i18next.t('provvigioni_dealer_euro');
    }
  }, [userRole])

  const renderHeader = useCallback(() => isSegnalatore ? (
    <tr>
      <th style={{ width: '80px', ...cellStyle }}>{i18next.t('type')}</th>
      <th style={{ width: '150px', ...cellStyle }}>{i18next.t('name')}</th>
      <th style={{ width: '120px', ...cellStyle }}>{i18next.t('created_at')}</th>
      <th style={{ width: '200px', ...cellStyle }}>{i18next.t('phone')}</th>
      <th style={{ width: '130px', ...cellStyle }}>{i18next.t('business_fiscal_code')}</th>
      <th style={{ width: '130px', ...cellStyle }}>{i18next.t('fiscal_code')}</th>
      <th style={{ width: '180px', ...cellStyle }}>{i18next.t('email')}</th>
      <th style={{ width: '150px', ...cellStyle }}>{i18next.t('marca')}</th>
      <th style={{ width: '120px', ...cellStyle }}>{i18next.t('modello')}</th>
      <th style={{ width: '100px', ...cellStyle }}>{i18next.t('stato')}</th>
      <th style={{ width: '150px', ...cellStyle }}>{i18next.t('commissions')}</th>
    </tr>
  ) : (
    <tr>
      <th style={{ width: '200px', ...cellStyle }}>{i18next.t('opportunity')}</th>
      <th style={{ width: '150px', ...cellStyle }}>{i18next.t('vehicle')}</th>
      <th style={{ width: '120px', ...cellStyle }}>{i18next.t('state')}</th>
      <th style={{ width: '220px', ...cellStyle }}>{labelProvvigioniSegnalatoreByRole}</th>
      {showProvvigioniDealer && <th style={{ width: '150px', ...cellStyle }}>{labelProvvigioniDealerByRole}</th>}
    </tr>
  ), [isSegnalatore, labelProvvigioniSegnalatoreByRole, labelProvvigioniDealerByRole, showProvvigioniDealer]);

  const renderRow = useCallback((row, index) => {
    return isSegnalatore
      ? (
        <tr key={index} className={index >= 5 ? 'visible-row' : ''}>
          <td style={{ width: '80px', ...cellStyle }} title={row.recordType}>{row.recordType}</td>
          <td style={{ width: '150px', ...cellStyle }} title={row.opportunity}>{row.opportunity}</td>
          <td style={{ width: '120px', ...cellStyle }} title={row.createdDate}>{row.createdDate}</td>
          <td style={{ width: '200px', ...cellStyle }} title={row.phone}>{row.phone}</td>
          <td style={{ width: '130px', ...cellStyle }} title={row.partitaIva}>{row.partitaIva}</td>
          <td style={{ width: '130px', ...cellStyle }} title={row.codiceFiscale}>{row.codiceFiscale}</td>
          <td style={{ width: '180px', ...cellStyle }} title={row.email}>{row.email}</td>
          <td style={{ width: '100px', ...cellStyle }} title={row.modello}>{row.modello}</td>
          <td style={{ width: '150px', ...cellStyle }} title={row.marca}>{row.marca}</td>
          <td style={{ width: '100px', ...cellStyle }} title={row.stato}>{row.stato}</td>
          <td style={{ width: '150px', ...cellStyle }} title={row.provvigioniSegnalatore}>{row.provvigioniSegnalatore}</td>
        </tr>
      )
      : (
        <tr key={index} className={index >= 5 ? 'visible-row' : ''}>
          <td style={{ width: '200px', ...cellStyle }} title={row.opportunity}>{row.opportunity}</td>
          <td style={{ width: '150px', ...cellStyle }} title={row.vehicle}>{row.vehicle}</td>
          <td style={{ width: '120px', ...cellStyle }} title={row.status}>{row.status}</td>
          <td style={{ width: '220px', ...cellStyle }} title={row.provvigioniSegnalatore}>{row.provvigioniSegnalatore}</td>
          {showProvvigioniDealer && <td style={{ width: '150px', ...cellStyle }} title={row.provvigioniDealer}>{row.provvigioniDealer}</td>}
        </tr>
      );
  }, [isSegnalatore, showProvvigioniDealer])

  if (!rows?.length) {
    return <Alert variant="light">{i18next.t("empty_state_signaling_month")}</Alert>
  }

  return (
    <div>
      <div className="table-responsive p-1 pb-0">
        <table className="table table-sm table-sm-responsive mb-0 stats-reporter-table" style={{ width: "101%", tableLayout: "fixed" }}>
          <thead className="table-light">
            {renderHeader()}
          </thead>
          <tbody>
            {displayRows.map((row, index) => renderRow(row, index))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div className="text-center">
          <button
            className="btn btn-link text-decoration-none"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? (
              <>
                {i18next.t('show_less')} <FaChevronUp className="ms-1" />
              </>
            ) : (
              <>
                {i18next.t('show_more')} ({allRows.length - 5}) <FaChevronDown className="ms-1" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default PeriodOpportunityTable;
